import React, { useEffect, useState } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import Card from '../../components/common/Card/Card';
import ImagePlaceholder from '../../components/common/ImagePlaceholder/ImagePlaceholder';
import Modal from '../../components/common/Modal/Modal';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { WORKSHOPS_DATA } from '../../constants/workshopsData';
import './Workshops.css';

/**
 * Página de Talleres - Colegio Bertolt Brecht
 */
const Workshops = () => {
    const [selectedWorkshop, setSelectedWorkshop] = useState(null);
    const [galleryIndex, setGalleryIndex] = useState(0);

    const handleViewDetails = (workshop) => {
        setGalleryIndex(0);
        setSelectedWorkshop(workshop);
    };

    const closeModal = () => {
        setSelectedWorkshop(null);
        setGalleryIndex(0);
    };

    const gallery = selectedWorkshop?.gallery || [];

    const showPreviousPhoto = () => {
        if (gallery.length < 2) return;
        setGalleryIndex((current) => (current - 1 + gallery.length) % gallery.length);
    };

    const showNextPhoto = () => {
        if (gallery.length < 2) return;
        setGalleryIndex((current) => (current + 1) % gallery.length);
    };

    useEffect(() => {
        if (!selectedWorkshop || gallery.length < 2) return undefined;

        const handleArrows = (event) => {
            if (event.key === 'ArrowRight') {
                setGalleryIndex((current) => (current + 1) % gallery.length);
            }
            if (event.key === 'ArrowLeft') {
                setGalleryIndex((current) => (current - 1 + gallery.length) % gallery.length);
            }
        };

        window.addEventListener('keydown', handleArrows);
        return () => window.removeEventListener('keydown', handleArrows);
    }, [selectedWorkshop, gallery.length]);

    const currentPhoto = gallery[galleryIndex];

    return (
        <div className="workshops">
            <PageHeader
                title="Talleres"
                subtitle="Actividades extracurriculares que complementan la formación integral de nuestros estudiantes"
            />

            <div className="container">
                <section className="workshops__grid-section">
                    <div className="workshops__grid">
                        {WORKSHOPS_DATA.map((workshop, index) => (
                            <Card
                                key={workshop.id}
                                hoverable
                                className={`workshops__card animate-scale-in animate-delay-${Math.min(index + 1, 4)}`}
                            >
                                <div className="workshops__card-image">
                                    <ImagePlaceholder
                                        src={workshop.image}
                                        aspectRatio="4/3"
                                        icon={workshop.icon}
                                        alt={workshop.imageAlt || workshop.title}
                                    />
                                </div>
                                <div className={`workshops__card-title workshops__card-title--${workshop.color}`}>
                                    <h2>{workshop.title}</h2>
                                </div>
                                <div className="workshops__card-body">
                                    <p className="workshops__card-description">{workshop.description}</p>
                                    <h4>Beneficios:</h4>
                                    <ul className="workshops__card-benefits">
                                        {workshop.benefits.map((benefit) => (
                                            <li key={benefit}>{benefit}</li>
                                        ))}
                                    </ul>
                                    <button
                                        className="workshops__view-details"
                                        onClick={() => handleViewDetails(workshop)}
                                    >
                                        Ver detalles →
                                    </button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </section>

                <section className="workshops__info animate-fade-in">
                    <div className="workshops__info-content">
                        <h2>¿Por qué participar en nuestros talleres?</h2>
                        <div className="workshops__info-grid">
                            <div className="workshops__info-item">
                                <div className="workshops__info-icon">🌟</div>
                                <h3>Desarrollo Integral</h3>
                                <p>Complementan la formación académica con habilidades artísticas, deportivas y sociales</p>
                            </div>
                            <div className="workshops__info-item">
                                <div className="workshops__info-icon">👥</div>
                                <h3>Socialización</h3>
                                <p>Fomentan el trabajo en equipo y las relaciones interpersonales</p>
                            </div>
                            <div className="workshops__info-item">
                                <div className="workshops__info-icon">💪</div>
                                <h3>Confianza</h3>
                                <p>Desarrollan la autoestima y seguridad en las propias capacidades</p>
                            </div>
                            <div className="workshops__info-item">
                                <div className="workshops__info-icon">🎯</div>
                                <h3>Descubrimiento</h3>
                                <p>Ayudan a descubrir talentos y pasiones personales</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Modal
                isOpen={!!selectedWorkshop}
                onClose={closeModal}
                title={selectedWorkshop?.title || ''}
                size="lg"
            >
                {selectedWorkshop && currentPhoto && (
                    <div className="workshop-modal">
                        <div className="workshop-modal__gallery">
                            <div className="workshop-modal__stage">
                                <img
                                    src={currentPhoto.src}
                                    alt={currentPhoto.alt}
                                    className="workshop-modal__photo"
                                />
                                {gallery.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            className="workshop-modal__arrow workshop-modal__arrow--left"
                                            onClick={showPreviousPhoto}
                                            aria-label="Foto anterior"
                                        >
                                            <IoChevronBack />
                                        </button>
                                        <button
                                            type="button"
                                            className="workshop-modal__arrow workshop-modal__arrow--right"
                                            onClick={showNextPhoto}
                                            aria-label="Foto siguiente"
                                        >
                                            <IoChevronForward />
                                        </button>
                                        <span className="workshop-modal__counter">
                                            {galleryIndex + 1} / {gallery.length}
                                        </span>
                                    </>
                                )}
                            </div>
                            {gallery.length > 1 && (
                                <div className="workshop-modal__thumbs" role="list">
                                    {gallery.map((photo, index) => (
                                        <button
                                            key={photo.src}
                                            type="button"
                                            role="listitem"
                                            className={`workshop-modal__thumb ${index === galleryIndex ? 'workshop-modal__thumb--active' : ''}`}
                                            onClick={() => setGalleryIndex(index)}
                                            aria-label={`Ver foto ${index + 1}`}
                                        >
                                            <img src={photo.src} alt="" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="workshop-modal__description">
                            {selectedWorkshop.fullDescription.split('\n').map((paragraph, index) => (
                                paragraph.trim() && <p key={index}>{paragraph}</p>
                            ))}
                        </div>
                        <div className="workshop-modal__benefits">
                            <h3>Beneficios del taller:</h3>
                            <ul>
                                {selectedWorkshop.benefits.map((benefit) => (
                                    <li key={benefit}>{benefit}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Workshops;
